import { useEffect, useRef, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { Code, ConnectError } from '@connectrpc/connect'
import type { Member } from '@ebalance/proto/bffuser/v1/user_directory_model_pb'
import {
  type SubStepInstance,
  SubStepStatus,
  type WorkflowCase,
  WorkflowCaseStatus,
} from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import {
  ApproveSubStepStageRequestSchema,
  ApproveSubStepWithoutStagesRequestSchema,
  CancelSubStepApprovalRequestSchema,
  UndoSubStepCompletionRequestSchema,
} from '@ebalance/proto/bffuser/v1/workflow_case_service_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import { useAuth } from '@enechain/auth-js'
import {
  Box,
  HStack,
  Link,
  SecondaryButton,
  Switch,
  TertiaryButton,
  Text,
  Tooltip,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaCircleCheckSolid, FaGearRegular } from '@enechain/ecloud-icons'

import { ActionReasonText } from '~/components/common/ActionReasonText'
import { Badge } from '~/components/common/Badge'
import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { CompactAlert } from '~/components/common/CompactAlert'
import { type EnumBadgeSpec } from '~/components/common/EnumStatusBadge'
import { iconSize } from '~/constants/iconSize'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import {
  isWorkflowAutomationAvailable,
  useWorkflowAutomationAvailability,
} from '~/hooks/useWorkflowAutomationAvailability'
import { formatDateTimeFull } from '~/models/dateTime'
import { resolveLinkedPage } from '~/models/linkedPage'
import { resolveMemberDisplayName } from '~/models/member'
import { summarizeMemberNames } from '~/models/memberListDisplay'
import { approvalSettingsHref } from '~/models/workflowApprovalSettings'
import { localizeBuiltinWorkflowText } from '~/models/workflowBuiltinText'
import { ApprovalGateSection } from '~/pages/WorkflowCaseDetail/components/ApprovalGateSection'
import { SubStepCompleteDialog } from '~/pages/WorkflowCaseDetail/components/SubStepCompleteDialog'
import { SubStepMemoPanel } from '~/pages/WorkflowCaseDetail/components/SubStepMemoPanel'
import { useApproveSubStepStage } from '~/pages/WorkflowCaseDetail/hooks/useApproveSubStepStage'
import { useApproveSubStepWithoutStages } from '~/pages/WorkflowCaseDetail/hooks/useApproveSubStepWithoutStages'
import { useCancelSubStepApproval } from '~/pages/WorkflowCaseDetail/hooks/useCancelSubStepApproval'
import { useUndoSubStepCompletion } from '~/pages/WorkflowCaseDetail/hooks/useUndoSubStepCompletion'
import { formatNameWithRecordKinds } from '~/pages/WorkflowCaseDetail/models/approvalViewerRelation'
import {
  isManuallyCompletedPlanSubmission,
  isSubStepCompletedByLinkedPage,
  isViewerAssigneeNotApplicable,
  showsDemandAutoUnattributedNote,
  undoBlockedReason,
} from '~/pages/WorkflowCaseDetail/models/workflowCaseDetail'

// Stable apperrors message strings from backend UndoSubStepCompletion. The BFF
// propagates these verbatim so the frontend can map them to localized reasons
// without parsing free-form text.
const msgUndoBlockedByWorkflowCompleted =
  'workflow_case_usecase: operation rejected because case is already completed'
const msgUndoBlockedByLaterSubStep =
  'workflow_case_usecase: a later sub_step is complete or awaiting approval'
const msgUndoBlockedByPlanSubmission =
  'workflow_case_usecase: a plan submission sub_step cannot be undone'

type Props = {
  caseId: string
  bigStepDisplayOrder: number
  /**
   * 大ステップ内での表示番号 (1 始まり)。 design `demand/case-detail.html` は
   * `.substeps { counter-reset: substep }` + `.substep-title::before { content:
   * counter(substep) ". " }` で大ステップごとに 1. 2. … と採番する。
   * `subStep.displayOrder` は並び順の値で 1 始まりの連番とは限らないため、
   * 描画側 (`BigStepAccordion`) がソート後の index から渡す。
   */
  displayNumber: number
  subStep: SubStepInstance
  wfCase: WorkflowCase
  isReadonly: boolean
  /** 担当者名の解決・担当者エディタの選択肢に使う組織内メンバー一覧。 */
  members: Member[]
  /**
   * 一覧の要承認・要対応ボタンから詳細画面へ遷移した際の focus 対象かどうか
   * (spec AC-WFW-001 §243、2026-08-19 改訂 D5)。true の場合、mount 時に該当
   * 要素へ scrollIntoView + 視覚 focus する (AWAITING_APPROVAL → 承認ゲート欄
   * `ApprovalGateSection` / それ以外 (主に IN_PROGRESS) → 行全体)。
   */
  isFocusTarget?: boolean
  /**
   * 大ステップ内の最後のサブステップか。 最終行は下に区切る対象が無いので
   * 点線を出さない (design `demand/case-detail.html:116`
   * `.substep:last-child { border-bottom: none }`。 デザイナー指摘 2026-08-28
   * 「ステップが 1 つの場合は不要要素に見える」 — 1 件のときはその 1 件が
   * 最終行なので、 last 判定だけで両方のケースが揃う)。
   */
  isLast?: boolean
}

/**
 * 完了トグル (ラベル + switch) を行の右上に絶対配置した時に、 タイトル行が
 * その下へ潜り込まないよう確保する幅。
 *
 * design `.substep-check` は `position: absolute; top: 10px; right: 12px` で、
 * 本文 (`.substep-body`) を全幅にしたうえでトグルを重ねる作り。 全幅にするのは
 * 「対象画面を開く」 と 担当者 / メモ を 1 行に並べた時、 右端をトグルの右端と
 * 揃えるためで、 通常の flex item にすると担当者がトグルの左端で止まってしまう。
 *
 * design はタイトルとの重なりを許容している (z-index 2) が、 実装のサブステップ名は
 * 雛形由来で長くなりうるので、 タイトル行にだけこの幅を予約して重なりを避ける
 * ([design 差異]。 タイトルが短い時は見た目に影響しない)。
 * 内訳: ラベル最長「完了にする」/「承認を依頼」 (md 14px × 5 字 = 70px) + gap 6px
 * + switch 40px + 余白 12px。
 */
const COMPLETE_TOGGLE_RESERVE = '128px'

/**
 * 自動完了のサブステップ (ラベル「完了 (自動完了される)」) で予約する幅。
 * 内訳: md 14px × 全角 9 字 + 半角スペース・括弧 (≈ 140px) + gap 6px + switch 40px + 余白 12px。
 */
const COMPLETE_TOGGLE_RESERVE_AUTO = '200px'

/** focus 対象要素の視覚 focus リング (design source 無し、実装者判断)。 */
const FOCUS_RING_STYLE = {
  outline: '2px solid',
  outlineColor: 'Border.accentPrimary',
  outlineOffset: '2px',
}

/**
 * サブステップ status → バッジ表示仕様 (網羅 Record)。
 *
 * 共通の `EnumStatusBadge` は全メンバーにバッジがある前提 (`Record<E, EnumBadgeSpec>`)
 * だが、 COMPLETE はチェックマークで表しバッジを出さない (UNSPECIFIED も同様) ため
 * `null` を許す同等の形で宣言し、 描画は null でない時だけ Badge を出す。
 */
const SUB_STEP_STATUS_BADGE: Record<SubStepStatus, EnumBadgeSpec | null> = {
  [SubStepStatus.UNSPECIFIED]: null,
  [SubStepStatus.NOT_STARTED]: {
    status: 'neutral',
    labelKey: 'page:workflowCaseDetail.subStep.status.notStarted',
  },
  [SubStepStatus.IN_PROGRESS]: {
    status: 'info',
    labelKey: 'page:workflowCaseDetail.subStep.status.inProgress',
  },
  [SubStepStatus.AWAITING_APPROVAL]: {
    status: 'warning',
    labelKey: 'page:workflowCaseDetail.subStep.status.awaitingApproval',
  },
  [SubStepStatus.COMPLETE]: null,
  [SubStepStatus.APPROVAL_WITHDRAWN]: {
    status: 'neutral',
    labelKey: 'page:workflowCaseDetail.subStep.status.approvalWithdrawn',
  },
}

/**
 * SubStepRow はサブステップを 1 行のチェックボックス形式で描画する
 * (design/demand/case-detail.html `.substep` / `.ecl-checkbox` 準拠)。
 *
 * - NOT_STARTED: チェックボックス disabled・未チェック
 * - IN_PROGRESS: チェックボックス活性・未チェック。 チェック操作で完了 (`useToggleSubStep`) を呼ぶ
 * - AWAITING_APPROVAL: チェックボックス disabled・未チェック (design 準拠、
 *   実行者完了済みだが「完了」チェックはまだ付けない)。 実行者完了情報 + 承認ステージ + 「取消」を表示
 * - COMPLETE: チェックボックス disabled・チェック済。 undoBlockedReason が undefined (取消可能) なら完了取消ボタンを表示
 * - 自動スキップ (autoSkippedAt あり): チェックボックス disabled・チェック済 + 「自動」タグ + 証跡
 *
 * design と spec (「完了にする」ボタン) が食い違う場合は design (= UI 構造) を SoT とし、
 * 操作の意味 (実行者完了 / 承認 / 取消) は spec AC-007/AC-007a/b/c のまま維持する。
 */
export const SubStepRow = ({
  caseId,
  bigStepDisplayOrder,
  displayNumber,
  subStep,
  wfCase,
  isReadonly,
  members,
  isFocusTarget = false,
  isLast = false,
}: Props): React.ReactElement => {
  const automationAvailability = useWorkflowAutomationAvailability()
  const showAutomationDetail = isWorkflowAutomationAvailable(
    automationAvailability,
  )
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()
  const { t } = useTranslation()
  const { user } = useAuth()
  const status = subStep.status
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false)
  const rowRef = useRef<HTMLDivElement>(null)
  const approvalGateRef = useRef<HTMLDivElement>(null)
  // 一覧からの focus 遷移は 1 回きりの導線 (下記 useEffect のコメント参照)。
  // AC-WFW-039 の 30 秒 polling で他ユーザーの完了 / 承認を拾うと
  // `isAwaitingApproval` が変化し effect が再発火するため、 無操作の 30 秒境界で
  // scroll ジャンプ + focus 奪取 (承認メモ入力中の blur を含む) が起きていた。
  // 初回に一度 focus したら latch し、 以後の status 変化では再 focus しない。
  const hasFocusedRef = useRef(false)
  const { mutateAsync: approveAsync, isPending: isApprovePending } =
    useApproveSubStepStage(caseId)
  const {
    mutateAsync: approveWithoutStagesAsync,
    isPending: isApproveWithoutStagesPending,
  } = useApproveSubStepWithoutStages(caseId)
  const { mutateAsync: cancelAsync, isPending: isCancelPending } =
    useCancelSubStepApproval(caseId)
  const { mutateAsync: undoAsync, isPending: isUndoPending } =
    useUndoSubStepCompletion(caseId)

  const isPending =
    isApprovePending ||
    isApproveWithoutStagesPending ||
    isCancelPending ||
    isUndoPending
  const undoBlocked = undoBlockedReason(wfCase, bigStepDisplayOrder, subStep)
  const isUndoable = undoBlocked === undefined

  const undoButton = (
    <TertiaryButton
      aria-label={t('page:workflowCaseDetail.subStep.undo')}
      disabled={!isUndoable || isPending}
      onClick={() => {
        void handleUndoCompletion()
      }}
      size="sm"
    >
      {t('page:workflowCaseDetail.subStep.undo')}
    </TertiaryButton>
  )

  const linked = resolveLinkedPage(
    subStep.linkedPageKind,
    caseId,
    subStep.linkedPlanId,
    wfCase.bgCode,
  )

  const isAutoSkipped = subStep.autoSkippedAt !== undefined
  const isAwaitingApproval = status === SubStepStatus.AWAITING_APPROVAL
  const statusBadge = SUB_STEP_STATUS_BADGE[status] ?? null
  const isComplete = status === SubStepStatus.COMPLETE

  // 一覧からの focus 遷移 (spec AC-WFW-001 §243、D5): AWAITING_APPROVAL は
  // 承認ゲート欄 (`ApprovalGateSection`)、それ以外は行全体へ scrollIntoView +
  // 視覚 focus する。 一覧ボタン click 由来の 1 回きりの導線なので、mount 後の
  // `isFocusTarget` の変化までは追跡しない (トグル開閉のたびの再フォーカスは
  // BigStepAccordion がサブステップ行を都度アンマウントする現状の実装では
  // 避けられないが、初回遷移の要件を満たせば十分)。
  useEffect(() => {
    if (!isFocusTarget || hasFocusedRef.current) return
    const target = isAwaitingApproval ? approvalGateRef.current : rowRef.current
    // target 未 attach の間 (data 遅延ロード等) は latch せず、 attach 後の
    // 再発火で focus する。 focus できたら latch し、 以後の polling 由来の
    // status 変化では再 focus しない。
    if (target === null) return
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    target.focus()
    hasFocusedRef.current = true
  }, [isFocusTarget, isAwaitingApproval])
  // spec AC-003g / AC-08 (workflow-delete-spec-realignment design D-6):
  // the parent case was deleted while this sub-step was AwaitingApproval,
  // auto-cancelling its pending approval request. The recorded approval
  // history stays visible (ApprovalGateSection below), but approve/cancel
  // become inert.
  const isApprovalWithdrawn = status === SubStepStatus.APPROVAL_WITHDRAWN
  // spec AC-WFW-007c/d: 差戻し直後 (実行者が再度「完了にする」を押す前) は
  // status が IN_PROGRESS に戻るため、AwaitingApproval / ApprovalWithdrawn /
  // Complete 条件だけだと保持された「前回承認時の申し送り」が非表示になる。
  // 差戻された担当者が申し送りを最も読みたい期間に見えなくなるのを避けるため、
  // stage.previousNotes を持つ間は承認ゲート欄を表示継続する。
  const hasPreviousApprovalNotes =
    subStep.approvalStages.some((stage) => stage.previousNotes.length > 0) ||
    subStep.previousApprovalWithoutStagesNotes.length > 0

  // spec AC-WFW-007f (workflow-linked-page-completion-trigger design D1/D8):
  // リンク先画面ありサブステップ (対象 kind = models の isSubStepCompletedByLinkedPage)
  // は業務ワークフロー側の手動完了を非活性にし、 リンク先画面の業務確定/提出操作を
  // 完了経路の SoT に一元化する (どの経路も担当者以外を拒否しない。リンク先経路は
  // 担当者非該当の区分も付けない — AC-WFW-007e / 007f ※)。
  const completedByLinkedPage = isSubStepCompletedByLinkedPage(
    subStep.linkedPageKind,
  )
  const disabledByLinkedPage =
    !isReadonly && status === SubStepStatus.IN_PROGRESS && completedByLinkedPage

  // spec AC-WFW-007e: IN_PROGRESS で担当者が設定されていて操作者本人でない場合は、
  // 「完了にする」を活性のまま常時の注意表示と確認ダイアログで注意喚起する
  // (disabled + tooltip にしない。サーバーも担当者を理由に拒否しない)。
  // disabledByLinkedPage の時は手動完了自体が無いので出さない (AC-WFW-007f)。
  const assigneeNotApplicable =
    !isReadonly &&
    !disabledByLinkedPage &&
    status === SubStepStatus.IN_PROGRESS &&
    isViewerAssigneeNotApplicable(subStep.assigneeMemberIds, user?.memberId)

  // 担当者表示 (spec AC-WFW-005c、multi-select 化: issue #4550):「先頭 1 名 +
  // 他 N 名 + tooltip 全名」 (AC-WFW-001 の対応者列と同じ形式、Q8)。 表示名は
  // member 一覧から解決し、 解決できない ID は生の eauth member ID を画面に
  // 出さず '-' にフォールバックする (既存 assigneeDisplay と同じ扱い)。
  const assigneeNames = subStep.assigneeMemberIds.map(
    (id) => resolveMemberDisplayName(members, id) ?? '-',
  )
  const assigneeSummary = summarizeMemberNames(assigneeNames)
  const assigneeLabel =
    assigneeSummary.lead === undefined
      ? '—'
      : assigneeSummary.othersCount === 0
        ? assigneeSummary.lead
        : t('page:workflowCaseDetail.subStep.assigneeOthers', {
            name: assigneeSummary.lead,
            count: assigneeSummary.othersCount,
          })
  const assigneeText = (
    <Text color="Text.midEmphasis" fontSize="md">
      {t('page:workflowCaseDetail.subStep.assignee', { name: assigneeLabel })}
    </Text>
  )

  // design: 承認待ちの間はチェックボックスをチェック済にしない (実行者完了はチェックとは
  // 別軸の substep-gate 領域で表示する)。 完了 (承認なし or 全承認済) / 自動スキップのみチェック済。
  const checked = isAutoSkipped || isComplete
  /**
   * 行の右端の担当者が、下の自動スキップ証跡と**同じことを述べるだけ**になるか
   * (2026-09-16 デザイナー指摘「担当者の表示は両方必要？」)。
   *
   * 担当者が未設定だと右端は「—」になり、証跡の「担当者: 不要 (自動処理)」と
   * 二重になる。この時だけ右端を出さない。
   *
   * ⚠ **`isAutoSkipped` だけで判定してはいけない。** `autoSkippedAt` は
   * 「サブステップが自動処理された」ではなく**「承認ゲートが自動スキップされた」**
   * 証跡で、実行者の完了フラグとは直交する
   * (`go/internal/backend/domain/entity/master/sub_step_instance.go:31-37`
   * 「They are orthogonal to the executor-completion flag」)。**人が担当者として
   * 完了させ、その承認ゲートだけが自動スキップされた行にも立つ** —
   * `assigneeMemberIDs` は案件作成時にコピーされ以後クリアされない (同 :42-49)
   * ので、この経路では実在する担当者名が入っている。`isAutoSkipped` だけで
   * 消すと**その名前まで消える** (PR #8187 レビュー指摘)。
   */
  const isAssigneeDuplicateOfSkipEvidence =
    isAutoSkipped && assigneeSummary.lead === undefined
  // 出す / 出さないの判定理由は [[isAssigneeDuplicateOfSkipEvidence]] の JSDoc 参照。
  const assigneeNode =
    isAssigneeDuplicateOfSkipEvidence ? null : assigneeSummary.othersCount ===
      0 ? (
      assigneeText
    ) : (
      <Tooltip
        content={assigneeSummary.allNames.join('、')}
        placement="top"
        showArrow
      >
        {assigneeText}
      </Tooltip>
    )
  // 完了確認ダイアログを開いている間は checked prop を true に変化させ、
  // キャンセル時に false へ戻す (= ark-ui Checkbox は checked prop の値変化でのみ
  // 内部 DOM state を再同期するため、 常に false のままだとネイティブ click で
  // ついた checked 表示が cancel 後も残ってしまう。 design の
  // `cb.checked = false; cb.dispatchEvent(...)` に相当する React 側の対処)。
  const displayChecked = checked || isCompleteDialogOpen
  const disabled =
    isReadonly ||
    isPending ||
    isAutoSkipped ||
    isComplete ||
    isAwaitingApproval ||
    isApprovalWithdrawn ||
    status === SubStepStatus.NOT_STARTED ||
    disabledByLinkedPage

  const handleCancelApproval = async (memo: string): Promise<void> => {
    await cancelAsync(
      create(CancelSubStepApprovalRequestSchema, {
        workflowCaseId: caseId,
        bigStepDisplayOrder,
        subStepDisplayOrder: subStep.displayOrder,
        memo,
      }),
    )
  }

  const handleApproveStage = async (
    stageOrder: number,
    note?: string,
  ): Promise<void> => {
    await approveAsync(
      create(ApproveSubStepStageRequestSchema, {
        workflowCaseId: caseId,
        bigStepDisplayOrder,
        subStepDisplayOrder: subStep.displayOrder,
        stageOrder,
        note,
      }),
    )
  }

  const handleApproveWithoutStages = async (note?: string): Promise<void> => {
    await approveWithoutStagesAsync(
      create(ApproveSubStepWithoutStagesRequestSchema, {
        workflowCaseId: caseId,
        bigStepDisplayOrder,
        subStepDisplayOrder: subStep.displayOrder,
        note,
      }),
    )
  }

  const getUndoErrorMessage = (err: unknown): string => {
    if (err instanceof ConnectError && err.code === Code.FailedPrecondition) {
      // ConnectError.message is prefixed with "[code] " by connect-web.
      // Use rawMessage to compare the backend apperrors string verbatim.
      if (err.rawMessage === msgUndoBlockedByWorkflowCompleted) {
        return t('page:workflowCaseDetail.error.undoBlockedByCompletedCase')
      }
      if (err.rawMessage === msgUndoBlockedByPlanSubmission) {
        return t('page:workflowCaseDetail.error.undoBlockedByPlanSubmission')
      }
      if (err.rawMessage === msgUndoBlockedByLaterSubStep) {
        return t('page:workflowCaseDetail.error.undoBlockedByLaterSubStep')
      }
      return t('page:workflowCaseDetail.error.failedPrecondition')
    }
    return t('page:workflowCaseDetail.subStep.undoFailed')
  }

  const handleUndoCompletion = async (): Promise<void> => {
    try {
      await undoAsync(
        create(UndoSubStepCompletionRequestSchema, {
          workflowCaseId: caseId,
          bigStepDisplayOrder,
          subStepDisplayOrder: subStep.displayOrder,
        }),
      )
      showSuccessToast({
        title: t('page:workflowCaseDetail.subStep.undoToast'),
      })
    } catch (err) {
      showErrorToast({
        title: getUndoErrorMessage(err),
      })
    }
  }

  /**
   * このサブステップを完了しようとすると **承認申請になる**か。
   *
   * ドッグフーディング指摘 (#4565)「押す前に要承認だと分からない」 への対応。
   * 承認ステージは backend が承認モード = 要承認のときだけ view を組むので、
   * `approvalStages` が空でないことを要承認の代理として使う。
   * design `demand/case-detail.html` は完了 **switch** のラベル自体を「完了にする」 →
   * 「承認を依頼」 に差し替える形。 実装の checkbox は可視ラベルを持たないので、
   * 暫定として checkbox の右に予告を出し、 文言も操作を含む
   * 「チェックをして承認依頼する」 にしている (承認なしの行には何も出さない =
   * 2026-08-06 Des 裁定)。 **最終形は 8080 準拠の switch + ラベル差し替え**で、
   * そこへ寄せる時にこの予告は消える (2026-08-25 Des 方針、 段階移行)。
   *
   * 出すのは**これから押せる行だけ**。 完了済み・自動スキップ・承認待ち・取り下げ後や
   * 非活性の行では、 予告する操作自体が無いので出さない。
   *
   * ## [既知の穴] 要承認 かつ 承認段階 0 件の行では予告が出ない
   *
   * この代理判定は**厳密には要承認と一致しない**。 backend の
   * `validateStagesForMode` は要承認モードに段階数の**上限しか課さず 0 件を許す**
   * 一方、 `ResolveCompletionOutcome` は要承認なら段階数に関わらず必ず
   * `Awaiting` を返す (承認者が居なくても fail-closed で承認待ちに倒す)。
   * つまり「要承認 / 段階 0 件」 の行は `approvalStages` が空になり予告が出ないまま
   * 承認待ちへ遷移する = #4565 の事象がそのまま残る。
   *
   * 正しく塞ぐには完了時の遷移先か承認モードを BFF が明示的に返す必要があり
   * (現在の `SubStepInstance` はどちらも持たない)、 proto / backend / BFF に
   * またがる契約変更になるため本 PR では扱わない。
   */
  const showApprovalHint = subStep.approvalStages.length > 0 && !disabled

  /**
   * トグルの可視ラベル。 design 8080 `demand/case-detail.html` の
   * `.substep-check-lbl` が `off` = 「完了にする」 / `on` = 「完了」 を出し分け、
   * 要承認かつ未完了のときだけ JS が `off` 側を「承認を依頼」 に差し替える
   * (`_apprModeOfSubstep`)。 同じ規則にする。
   */
  /*
   * リンク先画面の業務操作で自動完了するサブステップ (AC-WFW-007f) は、 状態に関わらず
   * 「完了 (自動完了される)」 (2026-10-07 デザイナー指摘)。 switch は押せないのに
   * 「完了にする」 と出ていて、 なぜ押せないかが tooltip を開かないと分からなかった。
   * 完了後も同じ文言にして、 手で完了させたのではないことを残す。
   */
  const toggleLabel = completedByLinkedPage
    ? t('page:workflowCaseDetail.subStep.autoCompleteLabel')
    : displayChecked
      ? t('page:workflowCaseDetail.subStep.completedLabel')
      : showApprovalHint
        ? t('page:workflowCaseDetail.subStep.requestApproval')
        : t('page:workflowCaseDetail.subStep.completeToggle')

  // 削除済みは戻らない状態なので、 押せない理由はツールチップでだけ示す (spec AC-WFW-003m)。
  // 完了したワークフローの理由は同 AC の範囲外なので出さない。
  const deletedCaseReason =
    wfCase.status === WorkflowCaseStatus.DELETED
      ? t('page:workflowCaseDetail.subStep.deletedCaseTooltip')
      : undefined
  const linkedPageReason = disabledByLinkedPage
    ? t('page:workflowCaseDetail.subStep.linkedPageCompletionTooltip')
    : undefined
  const switchDisabledReason = deletedCaseReason ?? linkedPageReason

  // 完了取消の押せない理由 (spec AC-WFW-005e / conventions.md §操作できない時のボタンの扱い)。
  // 後続の完了取消か差戻しで再び押せる (b) は画面上の文、 戻らない (d)・削除済み等は
  // tooltip で示す。 undoBlockedReason が理由を 1 つだけ返すので (d) の時に (b) は出ない。
  const undoReasonText =
    undoBlocked === 'laterProgressed'
      ? t('page:workflowCaseDetail.subStep.undoReasonLaterProgressed')
      : undefined
  const undoReasonTooltip =
    undoBlocked === 'deleted'
      ? deletedCaseReason
      : undoBlocked === 'planSubmission'
        ? t('page:workflowCaseDetail.subStep.undoTooltipPlanSubmission')
        : undoBlocked === 'completedCaseNotLast'
          ? t('page:workflowCaseDetail.subStep.undoTooltip')
          : undefined

  const switchEl = (
    <Switch
      flexShrink={0}
      marginTop="2px"
      aria-label={localizeBuiltinWorkflowText(subStep.name, t)}
      checked={displayChecked}
      disabled={disabled}
      onCheckedChange={(details: { checked: boolean }) => {
        // ON 時のみ完了確認ダイアログを開く (OFF 側は disabled のため到達しない)
        if (details.checked) {
          setIsCompleteDialogOpen(true)
        }
      }}
    />
  )

  // native disabled のスイッチは共通 Tooltip が包みを Tab の止まり先にする。
  const switchWithTooltip = (
    <ButtonTooltip content={switchDisabledReason}>{switchEl}</ButtonTooltip>
  )

  return (
    <Box
      ref={rowRef}
      position="relative"
      // design `.substep` = `padding: 10px var(--space-3)` (= 10px 12px)。
      // 左右 12px は、 外側 `.substeps` の 24px と合わせて左右 36px の inset を
      // 分担する分 (デザイナー指摘 2026-08-28「右側の余白を左と合わせたい」)。
      // 横 padding が無いと 完了トグルが行の右端に貼り付き、 右だけ余白が消える。
      padding="10px 12px"
      // 色なしショートハンド (`borderBottom="1px dashed"`) は border-bottom-color を
      // currentColor に落とし、 同じページに他の辺ショートハンドがあると
      // rgba(0,0,0,0.87) で描かれる (PR #6516 で全 86 箇所を longhand 化した既知の罠)。
      // design は `1px dashed var(--border-low)` = #e0e0e0 = Border.lowEmphasis なので
      // longhand で色を明示する (デザイナー指摘 2026-08-28「点線の色をもう少し薄く」)。
      borderBottomWidth={isLast ? undefined : '1px'}
      borderBottomStyle={isLast ? undefined : 'dashed'}
      borderBottomColor={isLast ? undefined : 'Border.lowEmphasis'}
      _focus={FOCUS_RING_STYLE}
      tabIndex={-1}
    >
      {/* 完了トグルは行の**右上**に絶対配置する (design `.substep-check`
          = `position: absolute; top: 10px; right: 12px` + `flex-direction: row-reverse`
          でラベルが左・switch が右)。 本文を全幅にして、 下のアクション行の
          担当者 / メモをトグルの右端と揃えるための作り (COMPLETE_TOGGLE_RESERVE 参照)。

          8080 は `<label>` でトグルと文言を包むが、 ここでは **包まない**: 包むと
          switch のアクセシブル名が「完了にする」 になり、 1 ケースに 8 行並ぶ
          サブステップを読み上げで区別できなくなる。 `aria-label` はサブステップ名の
          ままにして、 文言は視覚的な補助に留める ([design 差異]。 2026-08-25)。 */}
      <HStack
        position="absolute"
        zIndex={2}
        top="10px"
        right="12px"
        align="center"
        gap="6px"
      >
        <Text
          color={disabled ? 'Text.midEmphasis' : 'Text.highEmphasis'}
          fontSize="md"
          whiteSpace="nowrap"
        >
          {toggleLabel}
        </Text>
        {switchWithTooltip}
      </HStack>

      <HStack align="flex-start" gap={3}>
        {/* 完了マーカー列 (design `.substep::before`): 行の左端に状態カラムを設け、
            完了時だけ ✓ を出す。 未完了でも幅を予約するのは、 出し入れでタイトルの x が
            動くと 完了 / 未完了 が混在した時に見出しがガタつくため (design 同旨)。 */}
        <Box
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          display="flex"
          width="32px"
          height="32px"
          marginTop="-4px"
        >
          {checked && (
            <FaCircleCheckSolid
              boxSize={iconSize.subStepCompleteMark}
              color="Text.positiveMid"
            />
          )}
        </Box>

        <VStack align="stretch" flex={1} gap={1} minWidth={0}>
          <HStack
            align="center"
            wrap="wrap"
            gap={2}
            paddingRight={
              completedByLinkedPage
                ? COMPLETE_TOGGLE_RESERVE_AUTO
                : COMPLETE_TOGGLE_RESERVE
            }
          >
            {/* textStyle="accordionTitle" = lg/semibold で意図的視覚変更 (14px→16px)。
              design case-detail.html:80 `.substep-title` = lg/600 が正、
              accordionTitle は lh 未指定 (最近接 role)、ユーザー承認済み。 */}
            <Text
              textStyle="accordionTitle"
              color={
                isComplete && !isAutoSkipped
                  ? 'Text.midEmphasis'
                  : 'Text.highEmphasis'
              }
            >
              {/* 採番 (design `.substep-title::before` = `content: counter(substep) ". "`
                  / mid-emphasis / `font-variant-numeric: tabular-nums` /
                  `margin-right: 2px`)。 大ステップごとに 1. 2. … と振る。
                  タイトルと同じ Text の中に置き、 折り返しても番号が本文から
                  切り離されないようにする。 */}
              <Text
                as="span"
                marginRight="2px"
                color="Text.midEmphasis"
                fontVariantNumeric="tabular-nums"
              >
                {`${displayNumber}. `}
              </Text>
              {localizeBuiltinWorkflowText(subStep.name, t)}
            </Text>

            {isAutoSkipped && (
              <Badge status="neutral">
                {t('page:workflowCaseDetail.subStep.autoSkip.badge')}
              </Badge>
            )}

            {/* 現在ステータス (spec AC-WFW-005 / design case-detail.html
              `renderSubstepStatusBadges`)。 enum → バッジ仕様は SUB_STEP_STATUS_BADGE の
              網羅 Record 1 か所で表す。 自動スキップ行は「自動」バッジだけを出す:
              backend は前のサブステップが未完了の間は自動スキップ行も NOT_STARTED
              を返しうる (workflow_case_state_derivation.go の判定順。 未活性の判定が
              auto-skipped より先) ため、 status だけに頼ると「自動」と「未着手」が
              並ぶ。 */}
            {!isAutoSkipped && statusBadge !== null && (
              <Badge status={statusBadge.status}>
                {t(statusBadge.labelKey)}
              </Badge>
            )}
          </HStack>

          {/* リンク先画面で完了する step の押せない理由 (spec AC-WFW-007f) は、 スイッチの
            ツールチップだけで出す (spec がツールチップを指定。 2026-10-14 にタイトル直下の
            常時の文は廃止。 UI 辞書 components/action-reason.md)。 トグルのラベルも
            「完了 (自動完了される)」 なので、 手で完了しないことは画面から読める。 */}

          {/* 担当者非該当の常時の注意表示 (spec AC-WFW-007e (a))。承認者非該当の注意表示と
            同じ Alert warning。 担当者の名前は注意文に入れない (2026-10-20 デザイナー指示:
            Alert に個別の値を並べない)。 同じ行の担当者表示に出ている (UI 辞書
            alert-placement.md §6)。 [spec 差異] spec AC-WFW-007e は「（担当者: {担当者名}）」
            を添えるとする */}
          {assigneeNotApplicable && (
            <CompactAlert
              status="warning"
              title={t('page:workflowCaseDetail.subStep.notAssigneeWarning')}
            />
          )}

          {/* 説明はタイトルの直下 (design `.substep-desc` = title の次要素、md /
            mid-emphasis)。 以前はメモ欄の下に置いていた。 */}
          {subStep.description !== undefined && subStep.description !== '' && (
            <Text color="Text.midEmphasis" fontSize="md">
              {localizeBuiltinWorkflowText(subStep.description, t)}
            </Text>
          )}

          {/* 帰属不明の扱い (spec AC-WFA-006)。 位置は design `renderDemandAutoNotes`
            (説明と同じ段、 説明がある時はその後ろ) */}
          {showAutomationDetail && showsDemandAutoUnattributedNote(subStep) && (
            <Text color="Text.midEmphasis" fontSize="md">
              {t('page:workflowCaseDetail.subStep.demandAutoUnattributedNote')}
            </Text>
          )}

          {/* アクション行 (design `.substep-actions`): 「対象画面を開く」 を左、
            メモを右端に寄せて 1 行に並べる。 担当者はこの行に入れず、 下の行に置く
            (2026-10-07 デザイナー指摘「担当者は、 ボタンの下に配置」)。 */}
          <HStack align="center" wrap="wrap" gap={2} marginTop={1}>
            {/* 関連画面を SecondaryButton 化して同一タブで開く (spec AC-WFW-006
              「同一タブ内で開く」)。design `.substep-ref` は新タブ前提のまま
              残置だが、design 追従は Des確認 #3118 の領分のため実装は spec
              優先 (design gap)。遷移先の WorkflowBackBar でワークフロー詳細へ
              SPA 遷移で戻る。 */}
            {/* 開くボタンが無い行は、 ボタンの位置 (左) に担当者を置く。 下の行に回すと
              1 行目の左が空いて、 担当者がメモの下に浮いて見える */}
            {linked === null && assigneeNode}
            {linked !== null && (
              <SecondaryButton asChild size="sm">
                <RouterLink to={linked.href}>
                  {t('page:workflowCaseDetail.subStep.linkedPage', {
                    label: t(linked.labelKey),
                  })}
                </RouterLink>
              </SecondaryButton>
            )}
            <Link asChild>
              <RouterLink
                rel="noopener noreferrer"
                target="_blank"
                to={approvalSettingsHref(
                  subStep.linkedPageKind,
                  wfCase.workflowTemplateId,
                )}
              >
                <FaGearRegular
                  boxSize={iconSize.gridInline}
                  aria-hidden="true"
                />
                {t('page:workflowCaseDetail.subStep.approvalSettingsLink')}
              </RouterLink>
            </Link>

            {/* design `.substep-actor-group` (flex:1 / justify-content:flex-end) 相当。
              **担当者はこの group に入れない** (2026-09-28 デザイナー指摘
              「担当者の表示はこの位置で良い？ メモと関係ないように見える」)。
              担当者は**ステップの属性** (誰が担当か) で、 メモ・取消者は**出来事の
              記録**。 別物を 12px の gap で隣接させると、 メモ chip の先頭が
              `{名前} のメモ` と人名で始まるため **担当者の値とメモの投稿者が
              結び付いて読める**。 担当者は開くボタンの下の行 (左) へ置き、 この
              group はメモ専用にして右端に残す。 */}
            <HStack
              align="center"
              justifyContent="flex-end"
              wrap="wrap"
              flex="1 1 auto"
              gap={3}
              minWidth={0}
            >
              {/* メモ表示・追加・編集・削除 (spec AC-005b、workflow-substep-memo design
                AC-01〜AC-10)。 read-only ガード (削除済/WF完了) 以外は全サブステップ
                状態で editable (AC-09、サブステップ自身の状態では制限しない)。 */}
              <SubStepMemoPanel
                bigStepDisplayOrder={bigStepDisplayOrder}
                caseId={caseId}
                isReadonly={isReadonly}
                members={members}
                memo={subStep.memo}
                memoAt={subStep.memoAt}
                memoBy={subStep.memoBy}
                subStepDisplayOrder={subStep.displayOrder}
                subStepName={localizeBuiltinWorkflowText(subStep.name, t)}
              />
            </HStack>
          </HStack>

          {/* 2 行目: 左に担当者 (開くボタンの真下)、 右に完了行 (メモの真下)。

            担当者表示 (spec AC-WFW-005c、multi-select 化: issue #4550): 雛形で
            確定した値を「先頭 1 名 + 他 N 名 + tooltip 全名」 で表示のみとし、
            業務ワークフロー画面からの変更・解除 UI は提供しない (issue #4545)。
            「担当者: {名前}」 の 1 つの文字列・1 つの書体 (md / regular) で出す
            (2026-10-07 デザイナー指摘「担当者：ぶち にし、 フォントスタイルは 1 つに
            統一 (weight regular)」)。 以前はラベル low-emphasis + 名前 semibold の
            2 書体 (design `.substep-assignee-label` / `-name`) だった。 区切りは
            spec (AC-WFW-007e「担当者: {担当者名}」) と同じ画面の自動スキップ証跡
            「担当者: 不要 (自動処理)」 に合わせて半角コロン + スペース。
            位置は開くボタンの下 (同日の追加指摘「担当者は、 ボタンの下に配置」)。
            開くボタンの隣に置いていた時の「左に余白 8px」 は、 隣に置かなくなったので外した。

            担当者を出す / 出さないの判定理由は [[isAssigneeDuplicateOfSkipEvidence]] の
            JSDoc に書いてある。 design も排他にしている: `demand/case-detail.html` の
            `renderAutoSubstep()` (L3091-3101) は自動完了・自動完了予定の行から
            `.substep-actor-group` (= 担当者 + メモ) を丸ごと remove し、
            `.substep-skip-actor` だけを残す。人が手動で完了させた場合は逆に
            actor-group を出す (L3136)。

            完了日時 (承認フロー無しで直接完了した場合) + 完了取消ボタン。
            design/demand/case-detail.html L1585 の `実施者：' + (comp.completedBy || '—')`
            準拠で、 実施者が特定できない場合も完了日時ブロック自体は表示し、
            実施者のみ '-' にフォールバックする (完了した事実が UI から消えない)。
            自動スキップで完了したサブステップも undoBlockedReason が undefined (取消可能) の場合に
            完了取消ボタンを表示する (AC-WFW-005e)。 右端に寄せ、 上の行の右端にある
            メモ (メモを追加) の真下に置く (2026-10-07 デザイナー指摘「完了と完了取消は
            メモを追加 ボタンの下、 右端に配置」)。 証跡 (差戻し / 自動スキップ /
            完了取消履歴) は補足情報なので、 この行より後ろに送る (2026-09-17 デザイナー指摘)。 */}
          {((linked !== null && assigneeNode !== null) || isComplete) && (
            <HStack align="center" wrap="wrap" rowGap={1} columnGap={3}>
              {linked !== null && assigneeNode}
              {isComplete && (
                <HStack align="center" wrap="wrap" gap={3} marginLeft="auto">
                  {subStep.completedAt !== undefined && (
                    <Text color="Text.lowEmphasis" fontSize="xs">
                      {t(
                        isManuallyCompletedPlanSubmission(subStep)
                          ? 'page:workflowCaseDetail.subStep.manuallyCompletedAt'
                          : 'page:workflowCaseDetail.subStep.completedAt',
                        {
                          date: formatDateTimeFull(
                            timestampDate(subStep.completedAt),
                          ),
                          // 解決できない場合も生の member ID (auth0|...) は表示しない
                          by: formatNameWithRecordKinds(
                            t,
                            resolveMemberDisplayName(
                              members,
                              subStep.completedBy,
                            ) ?? '-',
                            {
                              isAssigneeNotApplicable:
                                subStep.completedIsAssigneeNotApplicable,
                            },
                          ),
                        },
                      )}
                    </Text>
                  )}
                  {undoReasonTooltip === undefined ? (
                    undoButton
                  ) : (
                    <ButtonTooltip content={undoReasonTooltip}>
                      {undoButton}
                    </ButtonTooltip>
                  )}
                </HStack>
              )}
            </HStack>
          )}

          {/* 完了取消ボタンの直下に右端をそろえて出す */}
          {undoReasonText !== undefined && (
            <ActionReasonText textAlign="right">
              {undoReasonText}
            </ActionReasonText>
          )}

          {/* 差戻し証跡 (spec AC-WFW-007d) */}
          {subStep.cancelledAt !== undefined && (
            <HStack wrap="wrap" gap={3}>
              <Text color="Text.midEmphasis" fontSize="sm">
                {t('page:workflowCaseDetail.subStep.cancelled.actor', {
                  by: formatNameWithRecordKinds(
                    t,
                    resolveMemberDisplayName(members, subStep.cancelledBy) ??
                      '-',
                    {
                      isApproverNotApplicable:
                        subStep.cancelIsApproverNotApplicable,
                      isSelfApproval: subStep.cancelIsSelfApproval,
                    },
                  ),
                })}
              </Text>
              {subStep.cancelledAt !== undefined && (
                <Text color="Text.midEmphasis" fontSize="sm">
                  {t('page:workflowCaseDetail.subStep.cancelled.atLabel')}{' '}
                  {formatDateTimeFull(timestampDate(subStep.cancelledAt))}
                </Text>
              )}
              {subStep.cancelMemo !== undefined &&
                subStep.cancelMemo !== '' && (
                  <Text color="Text.midEmphasis" fontSize="sm">
                    {t('page:workflowCaseDetail.subStep.cancelled.memoLabel', {
                      memo: subStep.cancelMemo,
                    })}
                  </Text>
                )}
            </HStack>
          )}

          {/* 自動スキップ証跡 (spec AC-009/AC-010) */}
          {isAutoSkipped && (
            <HStack wrap="wrap" gap={3}>
              <Text color="Text.midEmphasis" fontSize="sm">
                {t('page:workflowCaseDetail.subStep.autoSkip.actor')}
              </Text>
              {subStep.autoSkippedAt !== undefined && (
                <Text color="Text.midEmphasis" fontSize="sm">
                  {t('page:workflowCaseDetail.subStep.autoSkip.skippedAtLabel')}{' '}
                  {formatDateTimeFull(timestampDate(subStep.autoSkippedAt))}
                </Text>
              )}
              {subStep.skipReason !== undefined &&
                subStep.skipReason !== '' && (
                  <Text color="Text.midEmphasis" fontSize="sm">
                    {t('page:workflowCaseDetail.subStep.autoSkip.reasonLabel', {
                      reason: subStep.skipReason,
                    })}
                  </Text>
                )}
            </HStack>
          )}

          {/* 完了取消履歴 (spec AC-WFW-005f、#10423)。完了取消のたびに、取り消した
            完了と完了取消 (Action=「完了取消」。design の「ラベル + 名前 + 日時」の形で
            ラベルを操作名にする) を 1 組ずつ古い順に並べ、取消後の
            InProgress 状態でも残す。取り消した完了が不明な行 (旧データから移した
            取消) は完了取消だけを出す。 */}
          {subStep.undoneCompletions.length > 0 && (
            <VStack align="start" gap={1}>
              {subStep.undoneCompletions.map((u) => (
                <HStack
                  key={`${u.undoneBy}-${u.undoneAt?.seconds ?? ''}`}
                  wrap="wrap"
                  gap={3}
                >
                  {u.completedAt !== undefined && (
                    <Text color="Text.midEmphasis" fontSize="sm">
                      {t('page:workflowCaseDetail.subStep.completedAt', {
                        date: formatDateTimeFull(timestampDate(u.completedAt)),
                        by: formatNameWithRecordKinds(
                          t,
                          resolveMemberDisplayName(members, u.completedBy) ??
                            '-',
                          {
                            isAssigneeNotApplicable:
                              u.completedIsAssigneeNotApplicable,
                          },
                        ),
                      })}
                    </Text>
                  )}
                  {u.undoneAt !== undefined && (
                    <HStack gap={2}>
                      <Text color="Text.midEmphasis" fontSize="sm">
                        {t('page:workflowCaseDetail.subStep.undoneBy', {
                          by:
                            resolveMemberDisplayName(members, u.undoneBy) ??
                            '-',
                        })}
                      </Text>
                      <Text color="Text.midEmphasis" fontSize="sm">
                        {formatDateTimeFull(timestampDate(u.undoneAt))}
                      </Text>
                    </HStack>
                  )}
                </HStack>
              ))}
            </VStack>
          )}

          {/* 承認ゲート (design `.substep-gate` / `.substep-extras` 相当、spec AC-007a/b/c)。
            spec AC-003g/AC-06 (D-6): 削除により取り下げられた (ApprovalWithdrawn)
            場合も承認履歴セクションは引き続き表示する (承認/差戻しボタンのみ無効化)。
            完了 (COMPLETE) 後も承認メモの証跡を表示する (AC-WFW-007d)。 */}
          {(subStep.approvalStages.length > 0 ||
            subStep.requiresApprovalWithoutStages ||
            subStep.approvalWithoutStages !== undefined ||
            subStep.previousApprovalWithoutStagesNotes.length > 0) &&
            (isAwaitingApproval ||
              isApprovalWithdrawn ||
              isComplete ||
              hasPreviousApprovalNotes) && (
              // ref は AWAITING_APPROVAL の focus 遷移 (spec AC-WFW-001 §243、D5)
              // 専用の scroll/focus target。 それ以外の状態でこのブロックが表示
              // される時 (完了後の証跡表示等) は isFocusTarget 自体が
              // AWAITING_APPROVAL 前提で立たないため無害。
              <Box
                ref={approvalGateRef}
                _focus={FOCUS_RING_STYLE}
                tabIndex={-1}
              >
                <ApprovalGateSection
                  actionDisabledTooltip={deletedCaseReason}
                  bigStepDisplayOrder={bigStepDisplayOrder}
                  caseId={caseId}
                  isActionable={isAwaitingApproval}
                  isPending={isPending}
                  isReadonly={isReadonly || isApprovalWithdrawn}
                  members={members}
                  onApprove={handleApproveStage}
                  onApproveWithoutStages={handleApproveWithoutStages}
                  onCancel={handleCancelApproval}
                  subStep={subStep}
                  workflowTemplateId={wfCase.workflowTemplateId}
                />
              </Box>
            )}
        </VStack>
      </HStack>

      {isCompleteDialogOpen && (
        <SubStepCompleteDialog
          bigStepDisplayOrder={bigStepDisplayOrder}
          caseId={caseId}
          isAssigneeNotApplicable={assigneeNotApplicable}
          isOpen={isCompleteDialogOpen}
          onClose={() => {
            setIsCompleteDialogOpen(false)
          }}
          subStep={subStep}
        />
      )}
    </Box>
  )
}
